import { ChevronDown, ChevronUp, Pencil, UserPen } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AttributeEditor } from '~/components/common/attribute-editor/attribute-editor';
import { isAttributeEditable } from '~/components/common/attribute-editor/attribute-value';
import { CopyIconButton } from '~/components/common/copy-icon-button';
import { EnrichmentOptContent } from '~/components/common/enrichment-opt-content';
import { ProductDetailsComparison } from '~/components/common/product-details-comparison/product-details-comparison';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { getImpactBadgeClass } from '~/constants/impact';
import type { FieldEditProvenance } from '~/features/catalog-operations/utils/attribute-edits';
import { formatEditValue } from '~/features/catalog-operations/utils/attribute-edits';
import type { ProductDetailAttribute, ProductOptimization } from '~/types/enrichment';
import { cn } from '~/utils/cn';
import { formatAuditTimestamp } from '~/utils/date';
import { buildAttributeDiff, flattenSections } from '~/utils/product-diff';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parses enriched product-details attributes from `opt.value`.
 * Three formats are handled in order:
 *  1. New snake_case JSON array: [{ section_name, attribute_name, attribute_value }]
 *  2. Old camelCase JSON array:  [{ sectionName, attributeName, attributeValue }]
 *  3. Legacy pipe-delimited string: "SectionName: AttrName: Value | ..."
 * Returns null for non-product-details content types.
 */
function parseEnrichedAttrs(opt: ProductOptimization): ProductDetailAttribute[] | null {
  if (opt.contentType !== 'product-details') return null;

  try {
    const parsed = JSON.parse(opt.value) as unknown;
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0] as Record<string, unknown>;

      if ('section_name' in first) {
        return (
          parsed as { section_name: string; attribute_name: string; attribute_value: string }[]
        ).map((item) => ({
          sectionName: item.section_name,
          attributeName: item.attribute_name,
          attributeValue: item.attribute_value,
        }));
      }

      if ('sectionName' in first) {
        return (
          parsed as { sectionName: string; attributeName: string; attributeValue: string }[]
        ).map((item) => ({
          sectionName: item.sectionName,
          attributeName: item.attributeName,
          attributeValue: item.attributeValue,
        }));
      }
    }
  } catch {
    // Not JSON — fall through to legacy pipe-delimited parsing below.
  }

  // Legacy: pipe-delimited "SectionName: AttrName: Value" string.
  return opt.value
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((segment) => {
      const parts = segment.split(': ');
      if (parts.length < 3) return null;
      const [sectionName, attributeName, ...rest] = parts as [string, string, ...string[]];
      return {
        sectionName,
        attributeName,
        attributeValue: rest.join(': '),
      } satisfies ProductDetailAttribute;
    })
    .filter((a): a is ProductDetailAttribute => a !== null);
}

// ---------------------------------------------------------------------------
// Descriptor views (Product Description only)
// ---------------------------------------------------------------------------

/** Which content the row shows for a field that opts into the descriptor toggle. */
export type DescriptorView = 'description' | 'search-descriptors' | 'phrase-descriptions';

/**
 * Wires the Product Description row to search-descriptor data fetched at the page
 * level. Kept to plain primitives (never search-descriptor domain
 * types) so this shared component stays agnostic of that feature's shape — the
 * caller (product-detail-page.tsx) owns all of that logic and hands over the
 * already-computed values.
 */
export interface DescriptorViewsProps {
  active: DescriptorView;
  onActiveChange: (next: DescriptorView) => void;
  /** Pre-merged value shown in the Lily AI panel for the "search descriptors" view —
   *  already pipe-joined so it renders through the existing `EnrichmentOptContent`
   *  bullet splitter unchanged. */
  searchDescriptorsValue: string;
  /** Disables the "Search Descriptors" toggle option with an explanatory tooltip. */
  searchDescriptorsDisabled?: { tooltip: string } | false;
  /** Level 3 phrase description shown in the Lily AI panel for the "phrase
   *  descriptions" view (two columns, against the Current description). */
  phraseDescriptionsValue: string;
  /** Level 3 failure reason, shown in the Lily AI panel when there's no value. */
  phraseDescriptionsError?: string | undefined;
  /** Disables the "Phrase Descriptions" toggle option with an explanatory tooltip. */
  phraseDescriptionsDisabled?: { tooltip: string } | false;
}

/** Builds the toggle's three options, applying the per-option disabled+tooltip
 *  state the caller computed for the two derived views. `description` is never
 *  disabled — it's the plain enrichment value, always renderable. */
function buildDescriptorViewOptions(
  descriptorViews: DescriptorViewsProps,
  t: (key: string) => string,
): { value: DescriptorView; label: string; disabled?: true; tooltip?: string }[] {
  return [
    { value: 'description', label: t('descriptorViews.description') },
    {
      value: 'search-descriptors',
      label: t('descriptorViews.searchDescriptors'),
      ...(descriptorViews.searchDescriptorsDisabled && {
        disabled: true,
        tooltip: descriptorViews.searchDescriptorsDisabled.tooltip,
      }),
    },
    {
      value: 'phrase-descriptions',
      label: t('descriptorViews.phraseDescriptions'),
      ...(descriptorViews.phraseDescriptionsDisabled && {
        disabled: true,
        tooltip: descriptorViews.phraseDescriptionsDisabled.tooltip,
      }),
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EnrichedAttributeRowProps {
  /** Optimization field to render — carries label, impact, content type, and values. */
  opt: ProductOptimization;
  /**
   * When true, the row is expanded by default, the chevron is hidden, and the
   * user cannot collapse it. Useful when the parent already controls visibility.
   */
  disableToggle?: boolean;
  /**
   * Controlled expansion. Pass both to let the parent decide which rows are open
   * (e.g. auto-expanding whatever its filter singles out); omit both and the row
   * keeps its own state.
   */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /**
   * Enables in-place editing of the Lily value. Only passed for enriched
   * products — there is nothing to override on a product that hasn't been
   * enriched yet. Content types that can't be round-tripped safely stay
   * read-only regardless (see `isAttributeEditable`).
   */
  onSave?: ((value: string) => void | Promise<void>) | undefined;
  /** Bound to the save mutation's pending state. */
  isSaving?: boolean;
  /**
   * Operator-override provenance for this field. `live` drives the "Edited"
   * marker; `history` lists every recorded edit including ones a re-run has
   * since reverted. Omit on surfaces that don't fetch provenance.
   */
  provenance?: FieldEditProvenance | undefined;
  /**
   * Layout for the `product-details` structured diff. Defaults to `'panels'`
   * (two capped columns, scroll-linked by default via a toggle); `'aligned'`
   * drops the inner scroll and locks each attribute's two sides to one grid row.
   * Opt-in per surface — the feed-audit and trial preview steps keep the compact
   * capped table.
   */
  comparisonLayout?: 'panels' | 'aligned';
  /**
   * Panels-only: show the attribute search inside the `product-details` diff.
   * Off by default so compact previews stay lean; the product detail page turns
   * it on to keep the jump-to-attribute it had before.
   */
  comparisonShowSearch?: boolean;
  /**
   * Enriched-only view: hide the "Current" (pre-enrichment) panel so only the
   * Lily AI value shows, full-width. Used by the product detail page's PDP-preview
   * toggle; the structured product-details diff honours it too.
   */
  hideCurrent?: boolean;
  /**
   * Optional status marker rendered beside the field label (e.g. a Feed Audit
   * Added/Changed/Removed/Unchanged chip). Purely presentational — omit on
   * surfaces that don't classify field-level change.
   */
  statusChip?: ReactNode;
  /**
   * Opts this row into the Description / Search Descriptors / Phrase Descriptions
   * toggle. Only ever passed for the `description` field's row — every other
   * caller omits it and rendering is unaffected.
   */
  descriptorViews?: DescriptorViewsProps | undefined;
  /** `data-testid` root (default `enriched-attribute-row-{field}`); toggle, values, edit controls derive from it. */
  testId?: string;
}

/**
 * Provenance panel under the Lily value: who last overrode this attribute, and
 * what every previous override was.
 *
 * Split out because there are two distinct claims to make and only one of them is
 * about the value on screen. A live edit means "the value you are reading is an
 * operator's, not the agent's". A stale edit means the opposite — the operator's
 * value was reverted by a re-run and what you are reading is the agent's again —
 * so it is labelled rather than shown as current.
 */
function EditProvenance({
  provenance,
  label,
  testId,
}: {
  provenance: FieldEditProvenance;
  label: string;
  testId: string;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const { live, history } = provenance;
  // `live` is the newest row when present, so the rest is strictly older.
  const older = live ? history.slice(1) : history;

  return (
    <div data-testid={testId} className="bg-surface-blue border-t px-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {live ? (
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <UserPen className="text-primary size-3.5 shrink-0" aria-hidden="true" />
            Edited by <span className="text-foreground font-medium">{live.editedBy}</span>
            <span aria-hidden="true">·</span>
            {formatAuditTimestamp(live.editedAt)}
          </p>
        ) : (
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <UserPen className="size-3.5 shrink-0" aria-hidden="true" />
            {/* The override existed but a re-run replaced it, so the value above is
                the agent's — saying "edited" unqualified would misattribute it. */}
            Previously edited — reverted by a later enrichment run
          </p>
        )}
        {older.length > 0 && (
          <Button
            type="button"
            variant="link"
            size="sm"
            data-testid={`${testId}-history-button`}
            onClick={() => {
              setShowHistory((open) => !open);
            }}
            className="h-auto cursor-pointer p-0 text-xs"
          >
            {showHistory ? 'Hide' : 'Show'} {older.length} earlier{' '}
            {older.length === 1 ? 'edit' : 'edits'}
          </Button>
        )}
      </div>

      {showHistory && (
        <ul data-testid={`${testId}-history`} className="mt-2 flex flex-col gap-2 border-t pt-2">
          {older.map((edit) => (
            <li
              key={`${edit.editedAt}-${edit.workflowId}`}
              className="text-muted-foreground text-xs"
            >
              <p className="flex flex-wrap items-center gap-1.5">
                <span className="text-foreground font-medium">{edit.editedBy}</span>
                <span aria-hidden="true">·</span>
                {formatAuditTimestamp(edit.editedAt)}
              </p>
              <p className="mt-0.5 break-words">
                <span className="line-through">{formatEditValue(edit.previousValue)}</span>
                <span aria-hidden="true"> → </span>
                <span className="text-foreground">{formatEditValue(edit.newValue)}</span>
              </p>
            </li>
          ))}
        </ul>
      )}
      <span className="sr-only">Provenance for {label}</span>
    </div>
  );
}

/**
 * Accordion row for a single enrichment optimization field. Expands to show
 * a side-by-side current vs Lily AI comparison. The `product-details` content
 * type renders a structured attribute diff (ProductDetailsComparison) instead
 * of the standard two-panel layout.
 */
export function EnrichedAttributeRow({
  opt,
  disableToggle = false,
  open: controlledOpen,
  onOpenChange,
  onSave,
  isSaving = false,
  provenance,
  comparisonLayout = 'panels',
  comparisonShowSearch = false,
  hideCurrent = false,
  statusChip,
  descriptorViews,
  testId = `enriched-attribute-row-${opt.field}`,
}: EnrichedAttributeRowProps) {
  const { t } = useTranslation('common');
  // `null` = not editing. Holds the draft in the wire format the API expects, so
  // saving needs no further serialisation.
  const [draft, setDraft] = useState<string | null>(null);
  // Field name as well as content type: a few fields the API rejects aren't in
  // FIELD_CONTENT_TYPE, so they render as plain text and would otherwise offer an
  // editor whose save is guaranteed to fail.
  const canEdit = !!onSave && isAttributeEditable(opt.contentType, opt.field);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(disableToggle);
  const isControlled = controlledOpen !== undefined;
  const isOpen = disableToggle || (isControlled ? controlledOpen : uncontrolledOpen);

  /** Routes the toggle to whichever owner holds the state. */
  function handleOpenChange(next: boolean) {
    if (isControlled) onOpenChange?.(next);
    else setUncontrolledOpen(next);
  }

  const enrichedAttrs = parseEnrichedAttrs(opt);
  const isProductDetails = opt.contentType === 'product-details';
  const impactBadgeClass = getImpactBadgeClass(opt.impact);

  // Defaults to the plain enrichment value when no descriptor toggle is wired up
  // (every caller but the Product Description row).
  const activeDescriptorView = descriptorViews?.active ?? 'description';
  // All three views share the Current | Lily AI grid; only the right-hand value
  // differs — enriched, `<default> | <phrases>`, or the Level 3 rewrite.
  const lilyDisplayValue = !descriptorViews
    ? opt.value
    : activeDescriptorView === 'search-descriptors'
      ? descriptorViews.searchDescriptorsValue
      : activeDescriptorView === 'phrase-descriptions'
        ? descriptorViews.phraseDescriptionsValue
        : opt.value;
  const phraseDescriptionsError =
    activeDescriptorView === 'phrase-descriptions' && !lilyDisplayValue
      ? descriptorViews?.phraseDescriptionsError
      : undefined;
  // Editing only makes sense against the real enriched value — suppressed for the
  // two derived views, which have nothing to save back to.
  const canEditInCurrentView = canEdit && activeDescriptorView === 'description';

  // Attribute totals for the collapsed header, so the size and the churn of the
  // diff are both readable without expanding the row. Product-details only —
  // every other content type is a single value with nothing to count.
  const currentAttrs = opt.currentAttributes;
  const summary = useMemo(() => {
    if (!isProductDetails || !enrichedAttrs) return null;
    const rows = flattenSections(buildAttributeDiff(currentAttrs ?? [], enrichedAttrs));
    if (rows.length === 0) return null;
    return {
      total: rows.length,
      changed: rows.filter((row) => row.status !== 'unchanged').length,
    };
    // `enrichedAttrs` is re-parsed from `opt.value` each render, so the memo keys
    // on the string it came from rather than the new array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductDetails, opt.value, currentAttrs]);

  return (
    <Collapsible
      open={isOpen}
      data-testid={testId}
      {...(!disableToggle && { onOpenChange: handleOpenChange })}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          className={cn(
            'flex w-full items-center justify-between px-5 py-4 text-left transition-colors',
            disableToggle ? 'cursor-default' : 'hover:bg-surface-blue cursor-pointer',
          )}
        >
          <div className="flex items-center gap-3">
            <span data-testid={`${testId}-label`} className="text-sm font-semibold">
              {opt.label}
            </span>
            {/* Optional per-field change classification (Feed Audit results). */}
            {statusChip}
            {/* Conditional: the sample-enrichment payload carries no impact
                scoring, and an unconditional badge renders as an empty pill. */}
            {opt.impact && (
              <Badge
                variant="outline"
                data-testid={`${testId}-impact-badge`}
                className={cn('rounded-sm text-xs font-medium', impactBadgeClass)}
              >
                {opt.impact}
              </Badge>
            )}
            {/* Muted attribute tally beside the title — how much there is to
                review, and how much of it actually moved. */}
            {summary && (
              <span data-testid={`${testId}-summary`} className="text-muted-foreground text-xs">
                {t('productDetailsComparison.titleSummary', {
                  count: summary.total,
                  changed: summary.changed,
                })}
              </span>
            )}
            {/* Only a live override is announced here. A collapsed row is a claim
                about the value it summarises, and a reverted edit is not that. */}
            {provenance?.live && (
              <Badge
                variant="outline"
                data-testid={`${testId}-edited-badge`}
                className="border-primary/30 bg-selected-row text-primary flex items-center gap-1 rounded-sm text-xs font-medium"
                title={`Edited by ${provenance.live.editedBy}`}
              >
                <UserPen className="size-3" aria-hidden="true" />
                Edited
              </Badge>
            )}
          </div>
          {/* The diff legend used to live here; it now sits inside the comparison
              table, where it also acts as a row filter — a control belongs with
              the rows it narrows, not in the header that hides them. */}
          <div className="flex items-center gap-4">
            {!disableToggle && (
              <>
                {isOpen ? (
                  <ChevronUp className="text-muted-foreground h-4 w-4 shrink-0" />
                ) : (
                  <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
                )}
              </>
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent data-testid={`${testId}-content`}>
        {isProductDetails && enrichedAttrs ? (
          <div className="px-5 pb-4">
            {/* Copy buttons live in the table's own column headers now, so each
                sits on the side it copies instead of in a separate strip. */}
            <ProductDetailsComparison
              currentAttrs={opt.currentAttributes ?? []}
              enrichedAttrs={enrichedAttrs}
              layout={comparisonLayout}
              showSearch={comparisonShowSearch}
              hideCurrent={hideCurrent}
              label={opt.label}
              testId={`${testId}-comparison`}
            />
          </div>
        ) : (
          <div className="px-5 pb-4">
            {/* Description / Search Descriptors / Phrase Descriptions — only
                wired up for the Product Description row. Right-aligned and
                sized to its own content rather than the row's full width, since
                it's a secondary view switch, not the row's primary control. */}
            {descriptorViews && (
              <div className="mb-3 flex justify-end">
                <SegmentedTabs
                  value={descriptorViews.active}
                  onChange={(next) => {
                    // A view switch mid-edit would otherwise leave a stale draft
                    // (of the wrong value) open once the operator flips back.
                    setDraft(null);
                    descriptorViews.onActiveChange(next);
                  }}
                  options={buildDescriptorViewOptions(descriptorViews, t)}
                  ariaLabel={t('descriptorViews.ariaLabel')}
                  testId={`${testId}-descriptor-tabs`}
                  className="w-auto"
                />
              </div>
            )}
            <div
              className={cn(
                'grid grid-cols-1 gap-3 transition-[grid-template-columns,gap] duration-300 ease-in-out motion-reduce:transition-none',
                hideCurrent ? 'md:grid-cols-[0fr_1fr] md:gap-0' : 'md:grid-cols-[1fr_1fr]',
              )}
            >
              {/* Current — plain text from the curation API. Collapses out (height
                when stacked, width at ≥md) in the enriched-only (PDP preview) view. */}
              <div
                className={cn(
                  'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none md:block',
                  hideCurrent ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
                )}
                aria-hidden={hideCurrent}
              >
                <div className="min-w-0 overflow-hidden rounded-xl border">
                  <div className="bg-surface-blue flex items-center justify-between gap-2 border-b px-4 py-2">
                    <p className="text-muted-foreground text-xs font-medium">Current</p>
                    <CopyIconButton
                      text={opt.currentValue}
                      label={`Copy current ${opt.label}`}
                      copyMessage={`Copied current ${opt.label}`}
                      testId={`${testId}-copy-current-button`}
                    />
                  </div>
                  <div
                    data-testid={`${testId}-current-value`}
                    className="max-h-100 overflow-y-scroll bg-white p-4"
                  >
                    {opt.currentValue ? (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {opt.currentValue}
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-sm leading-relaxed">-</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Lily AI Optimization — primary accent border to match design. */}
              <div className="border-primary min-w-0 overflow-hidden rounded-xl border">
                <div className="bg-surface-blue flex items-center justify-between gap-2 border-b px-4 py-2">
                  <p className="text-primary text-xs font-medium">Lily AI Optimization</p>
                  <div className="flex items-center gap-0.5">
                    <CopyIconButton
                      text={lilyDisplayValue}
                      label={`Copy enriched ${opt.label}`}
                      copyMessage={`Copied enriched ${opt.label}`}
                      testId={`${testId}-copy-enriched-button`}
                    />
                    {canEditInCurrentView && draft === null && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${opt.label}`}
                        title={`Edit ${opt.label}`}
                        data-testid={`${testId}-edit-button`}
                        onClick={() => {
                          setDraft(opt.value);
                        }}
                        className="size-6 shrink-0 cursor-pointer"
                      >
                        <Pencil className="size-3.5" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
                {draft === null ? (
                  <>
                    <div
                      data-testid={`${testId}-enriched-value`}
                      className="max-h-100 overflow-y-scroll bg-white p-4"
                    >
                      {/* Search Descriptors view: one plain paragraph, rendered as-is
                            rather than through `EnrichmentOptContent` — that splitter
                            would misread the merged description+phrases string as a
                            pipe-delimited bullet list if it ever contained one. */}
                      {phraseDescriptionsError ? (
                        <p
                          data-testid={`${testId}-phrase-error`}
                          className="text-muted-foreground text-sm italic"
                        >
                          {t('deliveryWizard.preview.table.level3Failed', {
                            error: phraseDescriptionsError,
                          })}
                        </p>
                      ) : activeDescriptorView !== 'description' ? (
                        // Both derived views (search descriptors, Level 3) are
                        // plain prose — never split on `|` into bullets.
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {lilyDisplayValue || '-'}
                        </p>
                      ) : (
                        <EnrichmentOptContent
                          value={lilyDisplayValue}
                          type={opt.contentType}
                          testId={`${testId}-enriched-content`}
                        />
                      )}
                    </div>
                    {/* Hidden while editing, and while a derived view is active: the
                      draft/provenance both describe `opt.value`, not the merged
                      search-descriptors string. */}
                    {provenance && activeDescriptorView === 'description' && (
                      <EditProvenance
                        provenance={provenance}
                        label={opt.label}
                        testId={`${testId}-provenance`}
                      />
                    )}
                  </>
                ) : (
                  <div
                    data-testid={`${testId}-editor`}
                    className="flex flex-col gap-3 bg-white p-4"
                  >
                    <AttributeEditor
                      testId={`${testId}-attribute-editor`}
                      contentType={opt.contentType}
                      value={draft}
                      onChange={setDraft}
                      disabled={isSaving}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isSaving}
                        data-testid={`${testId}-cancel-button`}
                        onClick={() => {
                          setDraft(null);
                        }}
                        className={cn(isSaving ? 'cursor-not-allowed' : 'cursor-pointer')}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        isLoading={isSaving}
                        // Nothing changed means nothing to send.
                        disabled={isSaving || draft === opt.value}
                        data-testid={`${testId}-save-button`}
                        onClick={() => {
                          void (async () => {
                            try {
                              await onSave?.(draft);
                              // Close only on success; a failed save keeps the
                              // editor open so the operator's typing survives.
                              setDraft(null);
                            } catch {
                              // The mutation's onError already raised a toast.
                            }
                          })();
                        }}
                        className={cn(
                          isSaving || draft === opt.value ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
